/**
 * Identificação determinística da origem de uma imagem colada (Ctrl+V).
 *
 * 1) Preferência: o navegador fornece text/html no clipboard com o <img>
 *    original — extraímos o src diretamente.
 * 2) Fallback: compara os pixels da imagem colada com as imagens JÁ
 *    CARREGADAS na página (document.images) em canvas reduzido 32x32,
 *    RGB (alpha ignorado). Só considera correspondência com similaridade
 *    altíssima (>= 0.995) para nunca "chutar".
 *
 * O valor retornado (sourceImageUrl) é apenas uma PISTA enviada ao backend,
 * que valida caminho/tenant antes de afirmar qualquer correspondência.
 */

export function extractImgSrcFromClipboardHtml(clipboardData) {
  try {
    const html = clipboardData && clipboardData.getData && clipboardData.getData('text/html');
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const img = doc.querySelector('img[src]');
    const src = img && img.getAttribute('src');
    if (!src || src.startsWith('data:')) return null;
    return src;
  } catch (e) {
    return null;
  }
}

const THUMB = 32;
const EXACT_THRESHOLD = 0.995;

function drawToThumb(imgEl) {
  const canvas = document.createElement('canvas');
  canvas.width = THUMB;
  canvas.height = THUMB;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(imgEl, 0, 0, THUMB, THUMB);
  return ctx.getImageData(0, 0, THUMB, THUMB).data;
}

function similarity(a, b) {
  // Similaridade média por canal RGB (alpha ignorado), 1 = idêntico.
  let diff = 0;
  const px = THUMB * THUMB;
  for (let i = 0; i < px; i++) {
    const o = i * 4;
    diff += Math.abs(a[o] - b[o]) + Math.abs(a[o + 1] - b[o + 1]) + Math.abs(a[o + 2] - b[o + 2]);
  }
  return 1 - diff / (px * 3 * 255);
}

function loadImageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Compara a imagem colada (data URL) com as imagens carregadas na página e
 * retorna o src da melhor correspondência quase exata, ou null.
 */
export async function findPastedImageSourceOnPage(dataUrl) {
  try {
    const pasted = await loadImageFromDataUrl(dataUrl);
    const pastedPx = drawToThumb(pasted);
    let best = { sim: 0, src: null };
    const imgs = Array.from(document.images || []);
    for (const el of imgs) {
      if (!el.complete || !el.naturalWidth) continue;
      const src = el.currentSrc || el.src;
      if (!src || src.startsWith('data:')) continue;
      let px;
      try {
        px = drawToThumb(el); // canvas taint em imagem cross-origin → skip
      } catch (e) {
        continue;
      }
      const sim = similarity(pastedPx, px);
      if (sim > best.sim) best = { sim, src };
    }
    return best.sim >= EXACT_THRESHOLD ? best.src : null;
  } catch (e) {
    return null;
  }
}

/**
 * Resolve o sourceImageUrl de um paste: HTML do clipboard primeiro,
 * comparação local de pixels como fallback.
 */
export async function resolvePastedImageSource(clipboardData, dataUrl) {
  const fromHtml = extractImgSrcFromClipboardHtml(clipboardData);
  if (fromHtml) return fromHtml;
  if (dataUrl) return findPastedImageSourceOnPage(dataUrl);
  return null;
}
